import axios from 'axios';
import axiosInstance from './api';
import { blobToCoverImageFile } from '../components/Events/ManageEvent/coverImageUtils';
import {
  COVER_IMAGE_OPTIMIZATION_TIMEOUT_MS,
  REGENERATE_COVER_IMAGE_FIELD,
} from '../components/Events/ManageEvent/eventFormConstants';

const getApiErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (axios.isCancel(error)) return fallback;
  if (typeof error === 'string') return error;
  return error.message || error?.error?.message || error?.data?.message || fallback;
};

const parseBlobErrorMessage = async (blob) => {
  try {
    const text = await blob.text();
    const payload = JSON.parse(text);
    return payload?.message || payload?.error?.message || null;
  } catch {
    return null;
  }
};

/**
 * POST /admin/regenerate-cover-image — multipart upload; returns optimized PNG bytes.
 */
export const regenerateCoverImageFromFile = async (file, { signal } = {}) => {
  if (!(file instanceof File)) {
    throw new Error('Please select an image to upload.');
  }

  const formData = new FormData();
  formData.append(REGENERATE_COVER_IMAGE_FIELD, file);

  try {
    const response = await axiosInstance.post('/admin/regenerate-cover-image', formData, {
      responseType: 'blob',
      signal,
      timeout: COVER_IMAGE_OPTIMIZATION_TIMEOUT_MS,
    });

    const blob = response.data;
    const contentType = String(response.headers?.['content-type'] ?? '');

    if (!(blob instanceof Blob) || blob.size === 0) {
      throw new Error('Empty image returned from server.');
    }

    if (contentType.includes('application/json')) {
      const message = (await parseBlobErrorMessage(blob)) || 'Failed to optimize cover image.';
      throw new Error(message);
    }

    return blobToCoverImageFile(blob);
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }

    throw new Error(getApiErrorMessage(error, 'Failed to optimize cover image'));
  }
};
